import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { VERDICTS, type Verdict } from "../../studio/studioTypes"

/**
 * The verdicts a Studio V2 control may OFFER for setting. Exactly the five
 * backend codes (`studioTypes.VERDICTS`, byte-pinned to the backend's
 * VERDICT_INDEX) — labels and severity come from the existing
 * `review/verdict.ts::verdictMeta`, not a second map.
 *
 * WHY THERE IS NO "na" ("Nicht anwendbar") HERE. The design concept carries a
 * sixth verdict for statements like "the restriction is not in force, no action
 * needed". It deliberately does NOT become a settable code:
 *   - mapping it onto `unknown` would turn a calm, answered statement into a
 *     red needs-input stop that demands a written override comment
 *     (`classifyStatement` treats `unknown` as THE unanswered state), and
 *   - inventing a sixth wire value would break the FE/BE verdict-index pin.
 * A not-applicable statement is stored as `compliant`; the "not applicable"
 * framing stays where the supplier put it — in the statement's own basis text,
 * which the unit card quotes ("laut vorliegender Erklärung"). Tested in
 * verdictDisplay.test.ts so a later control cannot quietly widen this list.
 */
export const SETTABLE_VERDICTS = VERDICTS

/**
 * ONE verdict → ONE appearance, for every surface that draws a verdict: the
 * unit card's select, the head pill, and the navigator's dot.
 *
 * PRESENTATION ONLY — meaning and wording still come from `verdictMeta`, the
 * one meta map. What lives here is the design's palette decision, and it is
 * keyed per verdict because the design draws five distinguishable states while
 * severity draws three. Routing colour through severity collapsed `pending`
 * onto the amber "mit Bedingungen" tint, so a declaration nobody had assessed
 * looked like one carrying disclosed exceptions.
 *
 * Both fields must stay in step: they are the same decision rendered twice
 * (a CSS class where the design ships one, a variable where it paints a dot).
 * Splitting them across two modules is exactly how the navigator dot and the
 * mask came to disagree once already.
 */
const VERDICT_VISUAL: Record<Verdict, { tint: string; dotVar: string }> = {
  compliant: { tint: "sv2-v-ok", dotVar: "--green" },
  compliant_with_conditions: { tint: "sv2-v-exc", dotVar: "--attention" },
  non_compliant: { tint: "sv2-v-no", dotVar: "--red" },
  // Colour follows the LABEL's identity in the design source (`vcol()`), not
  // the backend code's name: `unknown` renders as "Unklar · nicht extrahiert"
  // — the prototype's `unk`, NEUTRAL — and `pending` as "Keine Aussage" — the
  // prototype's `none`, BLUE. An earlier cut had these two swapped because it
  // matched colours to the codes before the labels were aligned.
  unknown: { tint: "sv2-v-unk", dotVar: "--neutral" },
  pending: { tint: "sv2-v-none", dotVar: "--blue" },
}

const NO_VERDICT = { tint: "sv2-v-unk", dotVar: "--neutral" } as const

export function verdictVisual(verdict: Verdict | null): {
  tint: string
  dotVar: string
} {
  return verdict ? VERDICT_VISUAL[verdict] : NO_VERDICT
}

/**
 * STUDIO V2's OWN VERDICT LABEL — the prototype's exact German vocabulary
 * (`VERD[v].l` in the design source), which is NOT what v1's `verdictMeta`
 * says. v1's labels are half-English ("Compliant mit Bedingungen" —
 * `MC_DECL_VERDICT_COMPLIANT_WITH_CONDITIONS`), because that catalog serves
 * v1's own surfaces and was never meant to carry the prototype's wording.
 * Studio V2 needed its own key per verdict rather than editing v1's strings
 * in place, since `verdictMeta` still supplies SEVERITY (`ok`/`warn`/`fail`)
 * to v1 surfaces unchanged.
 *
 * Every v2 surface that NAMES a verdict (the unit card's `VerdictSelect`, the
 * collapsed unit's mini pill) reads its label through this function — one lookup, so no surface can
 * drift back to `verdictMeta`'s wording by accident.
 */
const VERDICT_LABEL_KEY: Record<Verdict, TranslationKeys> = {
  compliant: "MC_DECL_V2_VERDICT_COMPLIANT",
  compliant_with_conditions: "MC_DECL_V2_VERDICT_COMPLIANT_WITH_CONDITIONS",
  non_compliant: "MC_DECL_V2_VERDICT_NON_COMPLIANT",
  pending: "MC_DECL_V2_VERDICT_PENDING",
  unknown: "MC_DECL_V2_VERDICT_UNKNOWN",
}

export function verdictLabelKey(verdict: Verdict): TranslationKeys {
  return VERDICT_LABEL_KEY[verdict]
}

const CONDITIONED_VERDICTS: ReadonlySet<Verdict> = new Set([
  "compliant_with_conditions",
  "non_compliant",
])

/** Whether a card's own verdict names an exception with per-substance
 *  values to fill in (the STOFFE row, `SubstanceRow`, and the substances
 *  sheet behind it). Shared so `ReviewMaskPane` can close an already-open
 *  substances sheet the moment a verdict edit moves its card off this list,
 *  and `issueReasonKey` can word step 3 for a card without that row
 *  (TUR-1676), reusing this ONE predicate rather than a second copy of the
 *  verdict names that could drift out of sync with this one. */
export function hasConditionsRow(verdict: Verdict | null): boolean {
  return verdict !== null && CONDITIONED_VERDICTS.has(verdict)
}
